import { useMemo, useState } from 'react'
import { X, Clock } from 'lucide-react'
import { format, parseISO, isAfter, isBefore, subMinutes, addMinutes } from 'date-fns'
import { useTranslation } from 'react-i18next'
import type { Booking } from '@/lib/types/booking'
import { useUpdateBookingStatus } from '@/lib/queries/useBookings'

interface Props {
  todayBookings: Booking[]
}

export function ArrivalsWidget({ todayBookings }: Props) {
  const { t } = useTranslation()
  const updateStatus = useUpdateBookingStatus()
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())

  const now = new Date()

  const arrivals = useMemo(() =>
    todayBookings.filter((b) => {
      if (b.status !== 'approved') return false
      if (dismissed.has(b.documentId)) return false
      const bookingTime = parseISO(`${b.date}T${b.time}`)
      const windowStart = subMinutes(bookingTime, 10)
      const windowEnd   = addMinutes(bookingTime, b.duration ?? 90)
      return isAfter(now, windowStart) && isBefore(now, windowEnd)
    }),
  [todayBookings, dismissed])

  const dismiss = (id: string) => setDismissed((prev) => new Set(prev).add(id))
  const mutate  = (id: string, status: 'arrived' | 'no_show') =>
    updateStatus.mutate({ documentId: id, status })

  return (
    <div className="rounded-[var(--radius-lg)] bg-[var(--color-bg-surface)] border border-[var(--color-border-subtle)] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-border-subtle)]">
        <Clock className="h-3.5 w-3.5 text-[var(--color-accent-amber)]" />
        <p className="text-sm font-semibold text-[var(--color-text-primary)]">{t('arrivals.title')}</p>
        {arrivals.length > 0 && (
          <span className="ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]">
            {arrivals.length}
          </span>
        )}
      </div>

      {arrivals.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] text-center py-6">
          {t('arrivals.noArrivals')}
        </p>
      ) : (
        <div className="divide-y divide-[var(--color-border-subtle)]">
          {arrivals.map((b) => (
            <div key={b.documentId} className="p-3 space-y-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">{b.guest?.name ?? 'Walk-in'}</p>
                  <p className="text-xs text-[var(--color-text-muted)]">
                    {b.time} · {t('arrivals.guest', { count: b.people })}
                  </p>
                </div>
                <button
                  onClick={() => dismiss(b.documentId)}
                  className="shrink-0 p-1 rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  onClick={() => mutate(b.documentId, 'arrived')}
                  disabled={updateStatus.isPending}
                  className="h-8 rounded-[var(--radius-md)] text-xs font-medium bg-[var(--color-accent-green)] text-white hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer"
                >
                  {t('arrivals.arrived')}
                </button>
                <button
                  onClick={() => mutate(b.documentId, 'no_show')}
                  disabled={updateStatus.isPending}
                  className="h-8 rounded-[var(--radius-md)] text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  {t('arrivals.noShow')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
